import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { bookingApi } from '../../api/booking.api';

// Matches RateCard.tsx's Rate interface — the canonical UI Rate shape
export interface Rate {
  id: string;
  carrier: string;
  carrierCode: string;
  origin: string;
  destination: string;
  departureDate: string;
  arrivalDate: string;
  transitTime: number;
  totalPrice: number;
  currency: string;
  pricePerKg: number;
  chargeableWeight: number;
  breakdown: {
    freightCharge: number;
    fuelSurcharge: number;
    securityCharge: number;
    otherCharges: number;
  };
  features: string[];
  recommended?: boolean;
  source: string;
}

interface Booking {
  id: string;
  reference: string;
  awb: string;
  status: string;
  totalAmount: number;
  currency: string;
  originCity: string;
  destinationCity: string;
  departureDate: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AIInsights = Record<string, any> | null;

interface BookingState {
  quotes: { id: string; rates: Rate[]; currency: string } | null;
  aiInsights: AIInsights;
  selectedRate: Rate | null;
  bookings: Booking[];
  currentBooking: Booking | null;
  isLoading: boolean;
  loading: boolean;
  isSearching: boolean;
  error: string | null;
  totalBookings: number;
}

const initialState: BookingState = {
  quotes: null,
  aiInsights: null,
  selectedRate: null,
  bookings: [],
  currentBooking: null,
  isLoading: false,
  loading: false,
  isSearching: false,
  error: null,
  totalBookings: 0,
};

// Normalize API rate response to UI Rate shape
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeRate(r: any): Rate {
  return {
    id: r.id,
    carrier: r.airline?.name || r.carrier || 'Unknown',
    carrierCode: r.airline?.code || r.carrierCode || '',
    origin: r.origin?.code || r.origin || '',
    destination: r.destination?.code || r.destination || '',
    departureDate: r.departureDate,
    arrivalDate: r.arrivalDate,
    transitTime: r.transitTime,
    totalPrice: r.totalAmount ?? r.totalPrice ?? 0,
    currency: r.currency || 'GBP',
    pricePerKg: r.pricePerKg ?? (r.totalAmount ? r.totalAmount / (r.chargeableWeight || 100) : 0),
    chargeableWeight: r.chargeableWeight ?? 0,
    breakdown: {
      freightCharge: r.breakdown?.freightCharge ?? 0,
      fuelSurcharge: r.breakdown?.fuelSurcharge ?? 0,
      securityCharge: r.breakdown?.securitySurcharge ?? r.breakdown?.securityCharge ?? 0,
      otherCharges: r.breakdown?.otherCharges ?? 0,
    },
    features: r.features || [],
    recommended: r.recommended ?? false,
    source: r.source || 'DIRECT',
  };
}

export const searchRates = createAsyncThunk(
  'booking/searchRates',
  async (params: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await bookingApi.searchRates(params);
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      return rejectWithValue(err.response?.data?.error || 'Search failed');
    }
  },
);

export const fetchBookings = createAsyncThunk(
  'booking/fetchBookings',
  async (params: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
    try {
      const response = await bookingApi.getBookings(params);
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch bookings');
    }
  },
);

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    selectRate(state, action: PayloadAction<Rate>) {
      state.selectedRate = action.payload;
    },
    clearQuote(state) {
      state.quotes = null;
      state.aiInsights = null;
      state.selectedRate = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchRates.pending, (state) => {
        state.isSearching = true;
        state.loading = true;
        state.error = null;
        state.quotes = null;
        state.aiInsights = null;
      })
      .addCase(searchRates.fulfilled, (state, action) => {
        state.isSearching = false;
        state.loading = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = action.payload?.data ?? action.payload;
        const quoteId: string = payload?.id || '';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rawRates: any[] = payload?.rates || [];
        const rates = rawRates.map(normalizeRate);
        const currency = rates[0]?.currency || 'GBP';
        state.quotes = { id: quoteId, rates, currency };
        state.aiInsights = payload?.aiInsights ?? null;
      })
      .addCase(searchRates.rejected, (state, action) => {
        state.isSearching = false;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBookings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bookings = action.payload.data || [];
        state.totalBookings = action.payload.meta?.total || 0;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { selectRate, clearQuote, clearError } = bookingSlice.actions;
export default bookingSlice.reducer;
